import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { getUploadUrl } from '../utils/api';
import { getUser, logout } from '../utils/auth';
import { Plus, FileText, LogOut, Clock, Users, CheckCircle, Settings, Layers, GraduationCap, BookOpen, Calendar, Building2, Pencil, Trash2, XCircle, BarChart2, Archive, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const TeacherDashboard = () => {
  const navigate = useNavigate();
  const user = getUser();
  const [exams, setExams] = useState([]);
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);

  const fetchData = async () => {
    try {
      const [examsRes, schoolRes] = await Promise.all([
        api.get('/exams'),
        api.get('/school/info')
      ]);
      setExams(examsRes.data);
      setSchool(schoolRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user || user.role !== 'teacher') {
      navigate('/');
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const updateExamStatus = async (examId, status) => {
    const loadingToast = toast.loading(`${status === 'active' ? 'Activating' : 'Closing'} exam...`);
    try {
      await api.patch(`/exams/${examId}/status?status=${status}`);
      toast.dismiss(loadingToast);
      toast.success(`Exam ${status === 'active' ? 'activated' : 'closed'} successfully!`);
      fetchData();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };

  const deleteExam = async (examId, examTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${examTitle}"? This action cannot be undone.`)) {
      return;
    }
    const loadingToast = toast.loading('Deleting exam...');
    try {
      await api.delete(`/exams/${examId}`);
      toast.dismiss(loadingToast);
      toast.success('Exam deleted successfully!');
      fetchData();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(err.response?.data?.detail || 'Failed to delete exam');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-slate-50 text-slate-600 ring-slate-500/10',
      active: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
      closed: 'bg-red-50 text-red-700 ring-red-600/10',
    };
    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${styles[status]}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header with School Branding */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {school?.logo_url ? (
              <img src={getUploadUrl(school.logo_url)} alt={school.name} className="w-12 h-12 rounded-lg object-cover" />
            ) : school ? (
              <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-indigo-700" />
              </div>
            ) : null}
            <div>
              <h1 className="text-xl font-bold text-slate-900">{school?.name || 'Teacher Dashboard'}</h1>
              <p className="text-sm text-slate-600">Welcome, {user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="settings-btn"
              onClick={() => navigate('/teacher/settings')}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-all"
            >
              <Settings className="w-4 h-4" />
              Settings
            </button>
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 transition-all"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6 md:p-8">
        {/* Actions */}
        <div className="mb-8 flex flex-wrap items-center gap-3">
          <button
            data-testid="create-exam-btn"
            onClick={() => navigate('/teacher/create-exam')}
            className="flex items-center gap-2 px-6 py-2.5 bg-indigo-700 hover:bg-indigo-800 text-white font-medium rounded-lg shadow-sm transition-all"
          >
            <Plus className="w-5 h-5" />
            Create New Exam
          </button>
          <button
            data-testid="analytics-btn"
            onClick={() => navigate('/teacher/analytics')}
            className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-medium rounded-lg shadow-sm transition-all"
          >
            <BarChart2 className="w-5 h-5" />
            Class Analytics
          </button>
          <button
            data-testid="question-bank-btn"
            onClick={() => navigate('/teacher/question-bank')}
            className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg shadow-sm transition-all"
          >
            <BookOpen className="w-5 h-5" />
            Question Bank
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl border border-slate-100">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-indigo-100 rounded-lg">
                <FileText className="w-6 h-6 text-indigo-700" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Exams</p>
                <p className="text-2xl font-bold text-slate-900">{exams.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl border border-slate-100">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-emerald-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-emerald-700" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Active Exams</p>
                <p className="text-2xl font-bold text-slate-900">
                  {exams.filter(e => e.status === 'active').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl border border-slate-100">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-slate-100 rounded-lg">
                <Clock className="w-6 h-6 text-slate-700" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Draft Exams</p>
                <p className="text-2xl font-bold text-slate-900">
                  {exams.filter(e => e.status === 'draft').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl border border-slate-100">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-amber-100 rounded-lg">
                <Archive className="w-6 h-6 text-amber-700" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Archived</p>
                <p className="text-2xl font-bold text-slate-900">
                  {exams.filter(e => e.status === 'closed').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Exams List */}
        <div>
          <h2 className="text-2xl font-semibold text-slate-900 mb-6">Your Exams</h2>
          
          {loading ? (
            <div className="text-center py-12 text-slate-500">Loading exams...</div>
          ) : exams.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 mb-4">No exams created yet</p>
              <button
                onClick={() => navigate('/teacher/create-exam')}
                className="px-6 py-2.5 bg-indigo-700 hover:bg-indigo-800 text-white font-medium rounded-lg transition-all"
              >
                Create Your First Exam
              </button>
            </div>
          ) : (
            <>
              {/* Active and Draft Exams */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {exams.filter(e => e.status !== 'closed').map((exam) => (
                  <div
                    key={exam.id}
                    data-testid={`exam-card-${exam.id}`}
                    className="group bg-white border border-slate-200 rounded-xl p-6 hover:border-indigo-200 transition-all cursor-pointer"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <h3 className="text-lg font-semibold text-slate-900 group-hover:text-indigo-700 transition-colors">
                        {exam.title}
                      </h3>
                      {getStatusBadge(exam.status)}
                    </div>
                    
                    {/* Board/Grade/Subject tags */}
                    {(exam.board_name || exam.grade_name || exam.subject_name) && (
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {exam.board_name && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs">
                            <Layers className="w-3 h-3" />
                            {exam.board_name}
                          </span>
                        )}
                        {exam.grade_name && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-700 rounded text-xs">
                            <GraduationCap className="w-3 h-3" />
                            {exam.grade_name}
                          </span>
                        )}
                        {exam.subject_name && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-xs">
                            <BookOpen className="w-3 h-3" />
                            {exam.subject_name}
                          </span>
                        )}
                      </div>
                    )}
                    
                    <div className="space-y-2 text-sm text-slate-600 mb-4">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        <span>{exam.questions.length} questions</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>{exam.duration_minutes} minutes</span>
                      </div>
                      {/* Show if created by Data Entry Operator */}
                      {exam.teacher_id !== user?.id && exam.status === 'draft' && (
                        <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-2 py-1 rounded">
                          <Users className="w-4 h-4" />
                          <span className="text-xs font-medium">Created by Data Entry - Awaiting Activation</span>
                        </div>
                      )}
                      {/* Schedule Info */}
                      {(exam.start_datetime || exam.end_datetime) && (
                        <div className="pt-2 mt-2 border-t border-slate-100">
                          {exam.start_datetime && (
                            <div className="flex items-center gap-2 text-xs text-indigo-600">
                              <Calendar className="w-3.5 h-3.5" />
                              <span>Starts: {new Date(exam.start_datetime).toLocaleString()}</span>
                            </div>
                          )}
                          {exam.end_datetime && (
                            <div className="flex items-center gap-2 text-xs text-red-600 mt-1">
                              <Calendar className="w-3.5 h-3.5" />
                              <span>Ends: {new Date(exam.end_datetime).toLocaleString()}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-2">
                      {/* Draft exam actions: Edit, Delete, Activate */}
                      {exam.status === 'draft' && (
                        <>
                          <button
                            data-testid={`edit-exam-${exam.id}`}
                            onClick={() => navigate(`/teacher/edit-exam/${exam.id}`)}
                            className="flex-1 min-w-[80px] px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium rounded-lg transition-all flex items-center justify-center gap-1"
                          >
                            <Pencil className="w-4 h-4" />
                            Edit
                          </button>
                          <button
                            data-testid={`delete-exam-${exam.id}`}
                            onClick={() => deleteExam(exam.id, exam.title)}
                            className="px-3 py-2 bg-red-50 hover:bg-red-100 text-red-600 text-sm font-medium rounded-lg transition-all flex items-center justify-center gap-1"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          <button
                            data-testid={`activate-exam-${exam.id}`}
                            onClick={() => updateExamStatus(exam.id, 'active')}
                            className="flex-1 min-w-[80px] px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-all flex items-center justify-center gap-1"
                          >
                            <CheckCircle className="w-4 h-4" />
                            Activate
                          </button>
                        </>
                      )}
                      
                      {/* Active exam actions: Close only */}
                      {exam.status === 'active' && (
                        <button
                          data-testid={`close-exam-${exam.id}`}
                          onClick={() => updateExamStatus(exam.id, 'closed')}
                          className="flex-1 px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-all flex items-center justify-center gap-1"
                        >
                          <XCircle className="w-4 h-4" />
                          Close Exam
                        </button>
                      )}
                      
                      {/* Submissions button - always visible except for draft */}
                      {exam.status !== 'draft' && (
                        <button
                          data-testid={`view-submissions-${exam.id}`}
                          onClick={() => navigate(`/teacher/submissions/${exam.id}`)}
                          className="flex-1 px-3 py-2 bg-indigo-700 hover:bg-indigo-800 text-white text-sm font-medium rounded-lg transition-all flex items-center justify-center gap-1"
                        >
                          <Users className="w-4 h-4" />
                          Submissions
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Archived Exams Section */}
              {exams.filter(e => e.status === 'closed').length > 0 && (
                <div className="mt-8">
                  <button
                    onClick={() => setShowArchived(!showArchived)}
                    className="flex items-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-lg transition-all mb-4"
                    data-testid="toggle-archived-btn"
                  >
                    <Archive className="w-5 h-5" />
                    {showArchived ? 'Hide' : 'View'} Archived Exams ({exams.filter(e => e.status === 'closed').length})
                    {showArchived ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>

                  {showArchived && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {exams.filter(e => e.status === 'closed').map((exam) => (
                        <div
                          key={exam.id}
                          data-testid={`archived-exam-card-${exam.id}`}
                          className="group bg-slate-50 border border-slate-200 rounded-xl p-6 opacity-80"
                        >
                          <div className="flex items-start justify-between mb-4">
                            <h3 className="text-lg font-semibold text-slate-700">
                              {exam.title}
                            </h3>
                            {getStatusBadge(exam.status)}
                          </div>
                          
                          {/* Board/Grade/Subject tags */}
                          {(exam.board_name || exam.grade_name || exam.subject_name) && (
                            <div className="flex flex-wrap gap-1.5 mb-3">
                              {exam.board_name && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs">
                                  <Layers className="w-3 h-3" />
                                  {exam.board_name}
                                </span>
                              )}
                              {exam.grade_name && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-700 rounded text-xs">
                                  <GraduationCap className="w-3 h-3" />
                                  {exam.grade_name}
                                </span>
                              )}
                              {exam.subject_name && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-xs">
                                  <BookOpen className="w-3 h-3" />
                                  {exam.subject_name}
                                </span>
                              )}
                            </div>
                          )}
                          
                          <div className="space-y-2 text-sm text-slate-500 mb-4">
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4" />
                              <span>{exam.questions.length} questions</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4" />
                              <span>{exam.duration_minutes} minutes</span>
                            </div>
                          </div>

                          {/* Action Buttons for Archived */}
                          <div className="flex flex-wrap gap-2">
                            <button
                              data-testid={`view-submissions-${exam.id}`}
                              onClick={() => navigate(`/teacher/submissions/${exam.id}`)}
                              className="flex-1 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-all flex items-center justify-center gap-1"
                            >
                              <Users className="w-4 h-4" />
                              View Submissions
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeacherDashboard;